import cx from 'classnames'

import { Modal, useModals } from 'almanac-ui/Modal'
import { Heading } from 'blaze-ui/Heading/Heading'

import Styles from './VirtualMarketerPlansModal.module.scss'
import { Text } from 'blaze-ui/Text/Text'
import { Calendar } from 'blaze-ui/icons/32'
import { ImproveQuality } from 'blaze-ui/icons/36'
import { HighDetailedEngagement, Logout1 } from 'blaze-ui/icons'
import { Button } from 'blaze-ui/Button'
import { StripePrice, VirtualMarketerPlan } from '../types'
import { useState } from 'react'
import { Pressable } from '@react-aria/interactions'
import { CreditCardForm } from './components/CreditCardForm/CreditCardForm'
import { ButtonLink } from 'blaze-ui/Button/ButtonLink'
import { WORKSPACE_SELECTION_PATH } from 'paths'
import { useSession } from 'hooks/useSession'
import { useEnforceBlazeFreeTrial } from 'hooks/useEnforceBlazeFreeTrial'
import Review from 'blaze-ui/icons/32/Review'
import { useRootStore } from 'stores/RootStore'
import { useExperiment } from 'utils/experiments'
import { additionalSeatPrice } from '../utils'
import { useFlipperFlag } from 'hooks/useFlipperFlag'

const showBlackFridayOfferLabel = (price: StripePrice, isBlackFridayFlagOn: boolean) => {
  return price.recurring?.interval_count === 12 || (price.recurring?.interval_count === 1 && isBlackFridayFlagOn)
}

export const VirtualMarketerPlansModal = ({
  plan,
  isPaywall = false,
  onSuccess,
}: {
  plan: VirtualMarketerPlan
  isPaywall?: boolean
  onSuccess: () => void
}) => {
  const { closeModal } = useModals()
  const [selectedPrice, setSelectedPrice] = useState<StripePrice | undefined>(
    plan.prices.find((price: StripePrice) => {
      return price.recurring.interval_count === 12 && !price.metadata.additional_key
    }),
  )
  const [chosenPrice, setChosenPrice] = useState<StripePrice | null>(null)
  const { logout } = useSession()
  const { hasOtherPayingWorkspaces } = useEnforceBlazeFreeTrial()
  const { currentSubscription } = useRootStore().tierUsageStore
  const pricingVariant = useExperiment('pricing_order')
  const isBlackFriday = useFlipperFlag('black_friday_cta')

  const handleChoosePrice = async () => {
    if (!selectedPrice) return

    setChosenPrice(selectedPrice)
  }

  const handlePressOutside = () => {
    if (isPaywall) return

    closeModal()
  }

  if (chosenPrice) {
    return (
      <Modal.Root bodyClassName={Styles.modalBody} onPressOutside={handlePressOutside}>
        <CreditCardForm chosenPrice={chosenPrice} onBack={() => setChosenPrice(null)} onSuccess={onSuccess} />
      </Modal.Root>
    )
  }

  if (!selectedPrice) {
    return null
  }

  return (
    <Modal.Root bodyClassName={Styles.modalBody} onPressOutside={handlePressOutside}>
      {isPaywall ? (
        <Modal.Header>
          <div className={Styles.paywallHeader}>
            <Heading level={3}>Your plan has expired. Select a plan to continue.</Heading>
            <div className={Styles.controls}>
              {hasOtherPayingWorkspaces && (
                <ButtonLink variant="ghost" size="md" to={WORKSPACE_SELECTION_PATH}>
                  Switch Workspaces
                </ButtonLink>
              )}
              <Button
                variant="ghost"
                size="md"
                onPress={() => {
                  closeModal()
                  logout()
                }}
                frontIcon={Logout1}
              >
                Log Out
              </Button>
            </div>
          </div>
        </Modal.Header>
      ) : (
        <Modal.Header
          actions={
            <>
              {(currentSubscription?.trial_days_left || 0) > 0 && (
                <div className={Styles.largePill}>
                  <Heading level={5} color="var(--light-100)">
                    <strong>{currentSubscription?.trial_days_left || 0} days</strong> left in your free trial
                  </Heading>
                </div>
              )}
              {currentSubscription?.trial_ended && (
                <div className={cx(Styles.largePill, Styles.trialEnded)}>
                  <Heading level={5} color="var(--light-100)">
                    Your trial has ended
                  </Heading>
                </div>
              )}
            </>
          }
        >
          <div className={Styles.header}>
            <Heading level={3}>Save 15+ Hours Per Week</Heading>
            <Text color="var(--dark-60)">Keep growing while you focus on what matters most.</Text>
          </div>
        </Modal.Header>
      )}
      <Modal.Content>
        <div className={Styles.content}>
          <div className={Styles.sideBySideContent}>
            <div className={Styles.leftColumn}>
              <div className={Styles.top}>
                <div className={Styles.message}>
                  <Heading level={1} color="var(--purple-70)">
                    Marketing That Runs Itself
                  </Heading>
                  <Text color="var(--purple-70)">Choose your plan. We'll handle your marketing.</Text>
                </div>
                <div className={Styles.features}>
                  <div className={Styles.feature}>
                    <div className={Styles.icon}>
                      <Calendar />
                    </div>
                    <div className={Styles.featureText}>
                      <Text color="var(--purple-70)" fontWeight="700">
                        Never write posts again
                      </Text>
                      <Text color="var(--purple-70)"> – AI creates and posts for you.</Text>
                    </div>
                  </div>
                  <div className={Styles.feature}>
                    <div className={Styles.icon}>
                      <HighDetailedEngagement size={36} />
                    </div>
                    <div className={Styles.featureText}>
                      <Text color="var(--purple-70)" fontWeight="700">
                        Weekly content delivery
                      </Text>
                      <Text color="var(--purple-70)"> – Review and approve everything in under 10 minutes.</Text>
                    </div>
                  </div>
                  <div className={Styles.feature}>
                    <div className={Styles.icon}>
                      <ImproveQuality />
                    </div>
                    <div className={Styles.featureText}>
                      <Text color="var(--purple-70)" fontWeight="700">
                        Track what works
                      </Text>
                      <Text color="var(--purple-70)"> – See which content drives results.</Text>
                    </div>
                  </div>
                </div>
              </div>
              <div className={Styles.footer}>
                <Review />
                <Text variant="largeList" color="var(--dark-80)">
                  Add users for just ${additionalSeatPrice(plan, selectedPrice.recurring.interval_count)}/month in
                  Settings
                </Text>
              </div>
            </div>
            <div className={Styles.rightColumn}>
              {(() => {
                const filteredPrices = plan.prices.filter((price) => price?.metadata?.additional_key !== 'seat')
                const orderedPrices =
                  pricingVariant === 'reversed_order' ? [...filteredPrices].reverse() : filteredPrices
                return orderedPrices.map((price) => {
                  return (
                    <Pressable onPress={() => setSelectedPrice(price)} key={price.id}>
                      <div
                        className={cx(Styles.price, {
                          [Styles.selected]: selectedPrice.id === price.id,
                          [Styles.isBlackFridayOffer]: showBlackFridayOfferLabel(price, isBlackFriday),
                        })}
                        data-testid={price.id}
                      >
                        <div
                          className={cx(Styles.transparentBorder, { [Styles.selected]: selectedPrice.id === price.id })}
                        >
                          {showBlackFridayOfferLabel(price, isBlackFriday) && (
                            <span className={Styles.mostPopular}>
                              {isBlackFriday ? 'Black Friday Sale' : 'Most Popular'}
                            </span>
                          )}
                          <div className={Styles.planLeft}>
                            <Heading level={3}>
                              {price.recurring?.interval_count === 1
                                ? 'Monthly'
                                : `${price.recurring?.interval_count} month`}{' '}
                              plan
                            </Heading>
                          </div>
                          <div className={Styles.planRight}>
                            <div className={Styles.top}>
                              <Heading level={2}>
                                ${Math.ceil(price.unit_amount / 100 / price.recurring?.interval_count)}/mo
                              </Heading>
                              {(price.metadata.percent_off?.length || 0) > 0 && (
                                <div
                                  className={cx(Styles.percentOff, {
                                    [Styles.isBlackFridayOffer]: showBlackFridayOfferLabel(price, isBlackFriday),
                                  })}
                                >
                                  {price.metadata.percent_off}% off
                                </div>
                              )}
                            </div>
                            {price.recurring?.interval_count > 1 && (
                              <Text variant="secondary">${price.unit_amount / 100} total billed</Text>
                            )}
                          </div>
                        </div>
                      </div>
                    </Pressable>
                  )
                })
              })()}
            </div>
          </div>
        </div>
      </Modal.Content>
      <Modal.Footer className={cx({ [Styles.modalFooter]: !isPaywall })}>
        {!isPaywall && (
          <Modal.FooterContent slot="left">
            <Button variant="tertiary" size="lg" onPress={closeModal}>
              Back
            </Button>
            <div className={Styles.footerLeft}>
              <Text variant="placeholder">Don’t want to upgrade right now?</Text>
              <Text variant="placeholder">
                Even after your free trial, you can view and edit your existing content anytime.
              </Text>
            </div>
          </Modal.FooterContent>
        )}
        <Modal.FooterContent slot="right">
          <Button size="lg" onPress={handleChoosePrice}>
            Start Saving Time Today
          </Button>
        </Modal.FooterContent>
      </Modal.Footer>
    </Modal.Root>
  )
}
